// nextjs
import { reducerCases } from "./constants";
import { format, isToday, isYesterday, isThisWeek } from "date-fns";
import es from "date-fns/locale/es";
import { mergeInformacionContacto, mergeCrm } from "../util/utils";

const MESSAGE_STATUS_RANK = { sent: 1, delivered: 2, read: 3 };
const statusRank = (status) => MESSAGE_STATUS_RANK[status] || 0;

export const initialState = {
  contador: 0,
  conversations: [],
  conversationsPagination: { total: 0, limit: 50, offset: 0, hasMore: false },
  conversationsTotalCount: 0, // Total real de conversaciones según filtros (sin límite)
  current_phone: "",
  agentInfo: undefined,
  newUser: false,
  contactsPage: false,
  tagsPage: false, // Nueva página de etiquetas
  messageSearch: false,
  currentChatUser: undefined,
  socket: undefined,
  messages: [],
  userContacts: [],
  videoCall: undefined,
  voiceCall: undefined,
  incomingVoiceCall: undefined,
  incomingVideoCall: undefined,
  onlineUsers: [],
  contactSearch: "",
  filteredContacts: [],
  mobile: false,
  chatListPage: "Chat",
  scroll: "",
  file_chat: undefined,
  isChatExpired: false,
  globalStatus: "",
  connectedAgents: [],
  assignedAgent: undefined,
  activeFilters: null,
  isLoadingConversations: false, // Nuevo estado para controlar la carga
  activeListFilter: "all", // all | unread | favorites | tags
  activeTagFilter: null, // tagId en uso, solo cuando activeListFilter === 'tags'
  targetMessageId: null, // ID del mensaje objetivo para hacer scroll
  activeConversationFilter: null, // Filtro desde el cual se abrió la conversación activa { listFilter, tagFilter }
  pendingMessageErrors: {}, // Errores pendientes: { [messageId]: { phone, error } }
  forwardMessage: null, // Mensaje a reenviar
  typingUsers: {}, // { [chatKey]: { agentName, timestamp } }
  crmOpen: false,
  crmView: "resumen",
};

const formatDate = (timestamp) => {
  const date = new Date(timestamp);
  if (isToday(date)) return "Hoy";
  if (isYesterday(date)) return "Ayer";
  if (isThisWeek(date)) return format(date, "EEEE", { locale: es });
  return format(date, "dd/MM/yyyy", { locale: es });
};

const groupMessagesByDate = (messages) => {
  if (!messages || messages.length === 0) {
    return [];
  }

  // 1. Filtrar solo mensajes reales (eliminar todos los separadores de fecha existentes)
  const realMessages = messages.filter((msg) => msg && msg.msgtype !== "date");

  // 1.1 Deduplicar por _id para evitar claves repetidas en renderizados virtualizados
  const seenMessageIds = new Set();
  const uniqueRealMessages = [];
  for (const msg of realMessages) {
    const msgId = msg?._id ? msg._id.toString() : "";
    if (msgId) {
      if (seenMessageIds.has(msgId)) continue;
      seenMessageIds.add(msgId);
    }
    uniqueRealMessages.push(msg);
  }

  if (uniqueRealMessages.length === 0) {
    return [];
  }

  // 2. Ordenar mensajes por fecha (timestamp) de más antiguo a más reciente
  const sortedMessages = [...uniqueRealMessages].sort((a, b) => {
    const timeA = a.created_at || 0;
    const timeB = b.created_at || 0;
    return timeA - timeB;
  });

  // 3. Crear nuevos separadores de fecha basándose en los mensajes ordenados
  const groupedMessages = [];
  let lastDate = null;

  sortedMessages.forEach((message) => {
    if (!message.created_at) {
      // Si el mensaje no tiene fecha, agregarlo sin separador
      groupedMessages.push(message);
      return;
    }

    const messageDate = formatDate(message.created_at);
    if (messageDate !== lastDate) {
      // Crear un separador de fecha único usando el timestamp del primer mensaje del día
      groupedMessages.push({
        _id: `date-${message.created_at}-${messageDate}`,
        msgtype: "date",
        message: messageDate,
        created_at: message.created_at,
      });
      lastDate = messageDate;
    }
    groupedMessages.push(message);
  });

  return groupedMessages;
};

const normalizeValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const getChatKey = (phone, channel) => {
  const normalizedPhone = normalizeValue(phone);
  const normalizedChannel = normalizeValue(channel);
  if (!normalizedPhone) return "";
  return normalizedChannel
    ? `${normalizedPhone}::${normalizedChannel}`
    : normalizedPhone;
};

const splitChatKey = (chatKey) => {
  const normalizedKey = normalizeValue(chatKey);
  const separatorIndex = normalizedKey.indexOf("::");
  if (separatorIndex === -1) {
    return { phone: normalizedKey, channel: "" };
  }
  return {
    phone: normalizedKey.slice(0, separatorIndex),
    channel: normalizedKey.slice(separatorIndex + 2),
  };
};

const resolveChatKey = (state, payload = {}) => {
  const conversations = state.conversations || {};
  const directChatKey = normalizeValue(payload.chatKey || payload.current_phone);
  if (directChatKey && conversations[directChatKey]) {
    return directChatKey;
  }

  const phone = normalizeValue(payload.phone || payload.contact?.phone || payload.conversation?.phone || directChatKey);
  const channel = normalizeValue(
    payload.channel ||
      payload.current_channel ||
      payload.contact?.channel ||
      payload.conversation?.channel
  );

  if (phone && channel) {
    const composedKey = getChatKey(phone, channel);
    if (conversations[composedKey]) return composedKey;
    return composedKey;
  }

  if (phone && conversations[phone]) {
    return phone;
  }

  const candidates = Object.keys(conversations).filter((key) => {
    const conversation = conversations[key] || {};
    const parsed = splitChatKey(key);
    return normalizeValue(conversation.phone || parsed.phone) === phone;
  });

  if (candidates.length === 1) {
    return candidates[0];
  }

  if (candidates.length > 1) {
    const currentKey = normalizeValue(state.current_phone);
    if (currentKey && candidates.includes(currentKey)) {
      return currentKey;
    }

    const payloadChannel = normalizeValue(payload.channel);
    if (payloadChannel) {
      const phoneOnly = splitChatKey(phone).phone || phone;
      const composed = getChatKey(phoneOnly, payloadChannel);
      if (candidates.includes(composed)) return composed;
      return composed;
    }

    const payloadChatKey = normalizeValue(payload.chatKey);
    if (payloadChatKey && candidates.includes(payloadChatKey)) {
      return payloadChatKey;
    }
    if (payloadChatKey && payloadChatKey.includes("::")) {
      return payloadChatKey;
    }

    return candidates[0];
  }

  if (directChatKey) {
    return directChatKey;
  }

  return getChatKey(phone, channel);
};

const reducer = (state, action) => {
  const { type, ...payload } = action;

  switch (type) {
    case reducerCases.SET_USER_INFO:
      return {
        ...state,
        agentInfo: payload.agentInfo,
      };
    case reducerCases.SUMAR:
      return {
        ...state,
        contador: state.contador + 1,
      };
    case reducerCases.RESTAR:
      return {
        ...state,
        contador: state.contador - 1,
      };
    case reducerCases.SET_CONVERSATIONS: {
      const groupedConversations = Object.entries(payload.conversations || {}).reduce(
        (acc, [conversationPhone, conversation]) => {
          const parsed = splitChatKey(conversationPhone);
          const phone = normalizeValue(conversation?.phone || parsed.phone || conversationPhone);
          const channel = normalizeValue(conversation?.channel || parsed.channel);
          const chatKey = getChatKey(phone, channel);
          const existingConversation = state.conversations?.[chatKey] || state.conversations?.[conversationPhone];
          const groupedMessages = groupMessagesByDate(conversation.messages);
          acc[chatKey] = {
            ...conversation,
            phone,
            channel,
            chatKey,
            informacioncontacto: mergeInformacionContacto(
              conversation.informacioncontacto,
              existingConversation?.informacioncontacto
            ),
            crm: mergeCrm(conversation.crm, existingConversation?.crm),
            internalNoteAlert:
              conversation.internalNoteAlert ?? existingConversation?.internalNoteAlert ?? null,
            messages:
              existingConversation?.messages && existingConversation.messages.length > 0
                ? existingConversation.messages
                : groupedMessages,
          };
          return acc;
        },
        {}
      );
      return {
        ...state,
        conversations: groupedConversations,
        isLoadingConversations: false, // Marcar como cargado cuando se reciben las conversaciones
      };
    }
    case reducerCases.APPEND_CONVERSATIONS: {
      const current = { ...state.conversations };
      const toAppend = Object.entries(payload.conversations || {}).reduce(
        (acc, [conversationPhone, conversation]) => {
          const parsed = splitChatKey(conversationPhone);
          const phone = normalizeValue(conversation?.phone || parsed.phone || conversationPhone);
          const channel = normalizeValue(conversation?.channel || parsed.channel);
          const chatKey = getChatKey(phone, channel);
          if (current[chatKey]) {
            return acc; // evitar duplicados
          }
          acc[chatKey] = {
            ...conversation,
            phone,
            channel,
            chatKey,
            informacioncontacto: mergeInformacionContacto(
              conversation.informacioncontacto,
              current[chatKey]?.informacioncontacto
            ),
            crm: mergeCrm(conversation.crm, current[chatKey]?.crm),
            messages: groupMessagesByDate(conversation.messages),
          };
          return acc;
        },
        {}
      );
      const merged = { ...current, ...toAppend };
      return {
        ...state,
        conversations: merged,
      };
    }
    case reducerCases.UPDATE_CONVERSATION: {
      // Actualizar una conversación individual sin perder mensajes ya cargados
      if ((!payload.phone && !payload.chatKey) || !payload.conversation) {
        return state;
      }
      const chatKey = resolveChatKey(state, {
        ...payload,
        channel: payload.channel || payload.conversation?.channel,
      });
      const updatedConversations = { ...state.conversations };
      let remappedCurrentPhone = state.current_phone;
      let remappedCurrentChatUser = state.currentChatUser;

      // Limpiar claves alias del mismo chat (ej: phone vs phone::channel)
      // para evitar que quede visible un item viejo con estado desactualizado.
      const incomingPhone = normalizeValue(payload.conversation?.phone || payload.phone);
      const incomingChannel = normalizeValue(payload.conversation?.channel || payload.channel);
      const aliasKeys = Object.keys(updatedConversations).filter((key) => {
        if (key === chatKey) return false;
        const parsed = splitChatKey(key);
        const conv = updatedConversations[key] || {};
        const keyPhone = normalizeValue(conv.phone || parsed.phone);
        const keyChannel = normalizeValue(conv.channel || parsed.channel);
        return keyPhone === incomingPhone && keyChannel === incomingChannel;
      });

      for (const aliasKey of aliasKeys) {
        const aliasMessages = Array.isArray(updatedConversations[aliasKey]?.messages)
          ? updatedConversations[aliasKey].messages
          : [];
        const targetMessages = Array.isArray(updatedConversations[chatKey]?.messages)
          ? updatedConversations[chatKey].messages
          : [];
        const mergedMessages = aliasMessages.length > targetMessages.length
          ? aliasMessages
          : targetMessages;

        if (!updatedConversations[chatKey]) {
          updatedConversations[chatKey] = {
            ...updatedConversations[aliasKey],
            phone: incomingPhone,
            channel: incomingChannel,
            chatKey,
            messages: mergedMessages,
          };
        } else if (mergedMessages.length > 0) {
          updatedConversations[chatKey] = {
            ...updatedConversations[chatKey],
            messages: mergedMessages,
          };
        }

        // Si el chat activo estaba en una clave alias, moverlo a la clave canónica.
        if (state.current_phone === aliasKey) {
          remappedCurrentPhone = chatKey;
          remappedCurrentChatUser = {
            ...(state.currentChatUser || {}),
            chatKey,
            phone: incomingPhone || state.currentChatUser?.phone,
            channel: incomingChannel || state.currentChatUser?.channel,
          };
        }

        delete updatedConversations[aliasKey];
      }

      const existingConv = updatedConversations[chatKey];
      if (existingConv && Array.isArray(existingConv.messages) && existingConv.messages.length > 0) {
        // Preservar mensajes existentes, solo actualizar metadatos
        updatedConversations[chatKey] = {
          ...existingConv,
          ...payload.conversation,
          phone: payload.conversation.phone || existingConv.phone,
          channel: payload.conversation.channel || existingConv.channel,
          chatKey,
          messages: existingConv.messages, // Preservar mensajes cargados
          internalNoteAlert:
            payload.conversation.internalNoteAlert ?? existingConv.internalNoteAlert ?? null,
          // Actualizar metadatos
          lastMessage: payload.conversation.lastMessage !== undefined ? payload.conversation.lastMessage : existingConv.lastMessage,
          dateLastMessage: payload.conversation.dateLastMessage || existingConv.dateLastMessage,
          sourceLastMessage: payload.conversation.sourceLastMessage !== undefined ? payload.conversation.sourceLastMessage : existingConv.sourceLastMessage,
          statusLastMessage: payload.conversation.statusLastMessage !== undefined ? payload.conversation.statusLastMessage : existingConv.statusLastMessage,
          lastMessageId: payload.conversation.lastMessageId !== undefined ? payload.conversation.lastMessageId : existingConv.lastMessageId,
          errorLastMessage: payload.conversation.errorLastMessage !== undefined ? payload.conversation.errorLastMessage : existingConv.errorLastMessage,
          messagesUnread: payload.conversation.messagesUnread !== undefined ? payload.conversation.messagesUnread : existingConv.messagesUnread,
          // Actualizar otros campos
          name: payload.conversation.name || existingConv.name,
          username:
            payload.conversation.username !== undefined
              ? payload.conversation.username
              : existingConv.username,
          userId:
            payload.conversation.userId !== undefined
              ? payload.conversation.userId
              : existingConv.userId,
          dateControl: payload.conversation.dateControl || existingConv.dateControl,
          informacioncontacto: mergeInformacionContacto(
            payload.conversation.informacioncontacto,
            existingConv.informacioncontacto
          ),
          crm: mergeCrm(payload.conversation.crm, existingConv.crm),
          status: payload.conversation.status !== undefined ? payload.conversation.status : existingConv.status,
          pinned: payload.conversation.pinned !== undefined ? payload.conversation.pinned : existingConv.pinned,
          tagId: payload.conversation.tagId !== undefined ? payload.conversation.tagId : existingConv.tagId,
        };
      } else {
        // Nueva conversación o sin mensajes cargados, usar los mensajes de la nueva conversación
        const parsed = splitChatKey(chatKey);
        updatedConversations[chatKey] = {
          ...payload.conversation,
          phone: payload.conversation.phone || parsed.phone || payload.phone,
          channel: payload.conversation.channel || parsed.channel || payload.channel,
          chatKey,
          internalNoteAlert:
            payload.conversation.internalNoteAlert ?? existingConv?.internalNoteAlert ?? null,
          messages: groupMessagesByDate(payload.conversation.messages || []),
        };
      }

      const mergedConv = updatedConversations[chatKey];
      // Mantener userContacts al día (username/userId) sin esperar SET_USER_CONTACTS
      const updatedUserContacts = state.userContacts.map((contact) => {
        const contactKey =
          contact.chatKey ||
          (contact.channel ? `${contact.phone}::${contact.channel}` : contact.phone);
        const sameKey = contactKey === chatKey;
        const samePhoneChannel =
          normalizeValue(contact.phone) === incomingPhone &&
          (!incomingChannel ||
            !contact.channel ||
            normalizeValue(contact.channel) === incomingChannel);
        if (!sameKey && !samePhoneChannel) {
          return contact;
        }
        return {
          ...contact,
          chatKey: sameKey ? chatKey : contact.chatKey || chatKey,
          name: mergedConv?.name ?? contact.name,
          username:
            mergedConv?.username !== undefined
              ? mergedConv.username
              : contact.username,
          userId:
            mergedConv?.userId !== undefined ? mergedConv.userId : contact.userId,
        };
      });

      const isCurrentChat =
        state.current_phone === chatKey ||
        remappedCurrentPhone === chatKey ||
        (incomingPhone &&
          normalizeValue(state.currentChatUser?.phone) === incomingPhone &&
          (!incomingChannel ||
            !state.currentChatUser?.channel ||
            normalizeValue(state.currentChatUser?.channel) === incomingChannel));

      if (isCurrentChat) {
        remappedCurrentChatUser = {
          ...(remappedCurrentChatUser || state.currentChatUser || {}),
          chatKey,
          phone:
            incomingPhone ||
            remappedCurrentChatUser?.phone ||
            state.currentChatUser?.phone,
          channel:
            incomingChannel ||
            remappedCurrentChatUser?.channel ||
            state.currentChatUser?.channel,
          name:
            mergedConv?.name ??
            remappedCurrentChatUser?.name ??
            state.currentChatUser?.name,
          username:
            mergedConv?.username !== undefined
              ? mergedConv.username
              : remappedCurrentChatUser?.username ??
                state.currentChatUser?.username,
          userId:
            mergedConv?.userId !== undefined
              ? mergedConv.userId
              : remappedCurrentChatUser?.userId ?? state.currentChatUser?.userId,
        };
      }

      return {
        ...state,
        conversations: updatedConversations,
        userContacts: updatedUserContacts,
        current_phone: remappedCurrentPhone,
        currentChatUser: remappedCurrentChatUser,
      };
    }
    case reducerCases.SET_CONVERSATIONS_META:

      // El payload puede venir como { meta: {...} } o directamente como { total, limit, offset, hasMore }
      const metaData = payload.meta || payload;
      const rawLimit = Number(metaData?.limit) || 50;
      const rawOffset = Number(metaData?.offset) || 0;
      const newPagination = {
        total: Math.max(0, Number(metaData?.total) || 0),
        limit: Math.min(Math.max(1, rawLimit), 100),
        offset: Math.max(0, rawOffset),
        hasMore: Boolean(metaData?.hasMore ?? false),
      };

      return {
        ...state,
        conversationsPagination: newPagination,
      };
    case reducerCases.SET_CONVERSATIONS_TOTAL_COUNT:
      return { ...state, conversationsTotalCount: payload.totalCount || 0 };
    case reducerCases.SET_CURRENT_PHONE:
      if (!payload.current_phone) {
        return {
          ...state,
          current_phone: "",
          currentChatUser: undefined,
          assignedAgent: undefined,
          activeConversationFilter: null, // Limpiar el filtro cuando se cierra la conversación
        };
      }

      const selectedChatKey = resolveChatKey(state, {
        ...payload,
        current_phone: payload.current_phone,
      });
      const parsedSelected = splitChatKey(selectedChatKey);
      const selectedConversation = state.conversations[selectedChatKey] || {};
      const selectedPhone =
        payload.contact?.phone ||
        selectedConversation.phone ||
        parsedSelected.phone ||
        payload.current_phone;
      const selectedChannel =
        payload.contact?.channel ||
        selectedConversation.channel ||
        parsedSelected.channel ||
        payload.current_channel ||
        "";
      const mergedInformacionContacto = mergeInformacionContacto(
        payload.contact?.informacioncontacto,
        selectedConversation?.informacioncontacto
      );
      const mergedCrm = mergeCrm(payload.contact?.crm, selectedConversation?.crm);

      const mergedContact = {
        ...selectedConversation,
        ...payload.contact,
        phone: selectedPhone,
        channel: selectedChannel,
        chatKey: selectedChatKey,
        informacioncontacto: mergedInformacionContacto,
        crm: mergedCrm,
      };

      return {
        ...state,
        current_phone: selectedChatKey,
        currentChatUser: mergedContact,
        // Guardar el filtro desde el cual se abrió esta conversación
        activeConversationFilter: payload.activeListFilter !== undefined
          ? {
            listFilter: payload.activeListFilter,
            tagFilter: payload.activeTagFilter,
          }
          : state.activeConversationFilter, // Mantener el anterior si no se especifica
        conversations: {
          ...state.conversations,
          [selectedChatKey]: {
            ...selectedConversation,
            ...payload.contact,
            phone: selectedPhone,
            channel: selectedChannel,
            chatKey: selectedChatKey,
            informacioncontacto: mergedInformacionContacto,
            crm: mergedCrm,
          },
        },
        assignedAgent: undefined,
      };
    case reducerCases.ADD_MESSAGES_TO_CONVERSATION:
      if (!payload.phone && !payload.chatKey) {
        console.error("El teléfono no está definido en el payload.");
        return state; // Retorna el estado sin cambios
      }
      const targetChatKey = resolveChatKey(state, payload);
      const parsedTarget = splitChatKey(targetChatKey);
      const targetPhone = payload.phone || parsedTarget.phone;
      const updatedConversations = { ...state.conversations };
      if (!updatedConversations[targetChatKey]) {
        updatedConversations[targetChatKey] = {
          messages: [],
          messagesUnread: 0,
          phone: targetPhone,
          channel: payload.channel || parsedTarget.channel,
          chatKey: targetChatKey,
        };
      } else if (!Array.isArray(updatedConversations[targetChatKey].messages)) {
        updatedConversations[targetChatKey].messages = [];
      }

      // Verificar si el mensaje ya existe (evitar duplicados) con O(1) Set lookup
      const existingIds = new Set(
        updatedConversations[targetChatKey].messages
          .filter(msg => msg._id && msg.msgtype !== "date")
          .map(msg => msg._id.toString())
      );
      const messageExists = payload.idMessage && existingIds.has(payload.idMessage.toString());

      if (messageExists) {
        // El mensaje ya existe, no agregarlo de nuevo
        return state;
      }

      // Verificar si hay un error pendiente para este mensaje
      const pendingError = state.pendingMessageErrors?.[payload.idMessage];
      const updatedPendingErrors = { ...state.pendingMessageErrors };

      const newMessage = {
        _id: payload.idMessage,
        agent: payload.agent || null,
        message: payload.message,
        source: payload.source,
        created_at: payload.created_at,
        msgtype: payload.msgtype,
        urls3: payload.urls3,
        error: pendingError ? true : payload.errorMessage,
        errorstatus: pendingError ? pendingError.error : undefined,
        fileName: payload.fileName,
        replyMessage: payload.replyMessage,
        optionsT: payload.optionsT,
        file: payload.file,
        contactData: payload.contactData,
        room: payload.room || null,
        data: payload.data || null,
      };

      // Si se aplicó un error pendiente, removerlo
      if (pendingError) {
        delete updatedPendingErrors[payload.idMessage];
        console.log(`[ADD_MESSAGES_TO_CONVERSATION] Error pendiente aplicado a mensaje: ${payload.idMessage}`);
      }

      const newMessages = [
        ...updatedConversations[targetChatKey].messages,
        newMessage,
      ];

      updatedConversations[targetChatKey].messages =
        groupMessagesByDate(newMessages);

      // Actualizar lastMessage si es un mensaje enviado (source !== 1)
      if (payload.source !== 1 && Number(payload.source) !== 6) {
        updatedConversations[targetChatKey].lastMessageId = payload.idMessage ? String(payload.idMessage) : null;
        updatedConversations[targetChatKey].lastMessage = payload.message;
        updatedConversations[targetChatKey].dateLastMessage = payload.created_at;
        updatedConversations[targetChatKey].sourceLastMessage = payload.source;
        updatedConversations[targetChatKey].statusLastMessage = payload.status || "";
        updatedConversations[targetChatKey].errorLastMessage = pendingError ? pendingError.error : (payload.errorMessage ? (payload.errorstatus || payload.errorMessage) : null);
      }

      return {
        ...state,
        conversations: updatedConversations,
        pendingMessageErrors: updatedPendingErrors,
      };

    case reducerCases.ADD_PREVIOUS_MESSAGES_TO_CONVERSATION:
      if (!payload.phone && !payload.chatKey) {
        console.error("El teléfono no está definido en el payload.");
        return state; // Retorna el estado sin cambios
      }
      const previousChatKey = resolveChatKey(state, payload);
      const parsedPrevious = splitChatKey(previousChatKey);
      const updatedConversationsForPrevious = { ...state.conversations };
      if (!updatedConversationsForPrevious[previousChatKey]) {
        updatedConversationsForPrevious[previousChatKey] = {
          messages: [],
          messagesUnread: 0,
          phone: payload.phone || parsedPrevious.phone,
          channel: payload.channel || parsedPrevious.channel,
          chatKey: previousChatKey,
        };
      } else if (
        !Array.isArray(updatedConversationsForPrevious[previousChatKey].messages)
      ) {
        updatedConversationsForPrevious[previousChatKey].messages = [];
      }

      // Deduplicar mensajes con Set O(1) lookup antes de agrupar
      const existingPrevIds = new Set(
        updatedConversationsForPrevious[previousChatKey].messages
          .filter(msg => msg._id && msg.msgtype !== "date")
          .map(msg => msg._id.toString())
      );
      const uniqueNewMessages = payload.messages.filter(
        msg => !msg._id || !existingPrevIds.has(msg._id.toString())
      );
      const previousMessages = [
        ...uniqueNewMessages,
        ...updatedConversationsForPrevious[previousChatKey].messages,
      ];

      updatedConversationsForPrevious[previousChatKey].messages =
        groupMessagesByDate(previousMessages);

      return {
        ...state,
        conversations: updatedConversationsForPrevious,
      };

    case reducerCases.SET_ALL_CONTACTS_PAGE:
      return {
        ...state,
        contactsPage: !state.contactsPage,
      };
    case reducerCases.CONTACT_SEARCH: {
      const q = String(payload.contactSearch || "").toLowerCase();
      const filteredContacts = state.userContacts.filter((contact) => {
        const username = String(contact?.username || "")
          .toLowerCase()
          .replace(/^@/, "");
        return (
          contact?.name?.toLowerCase().includes(q) ||
          contact?.phone?.toLowerCase().includes(q) ||
          username.includes(q.replace(/^@/, ""))
        );
      });
      return {
        ...state,
        contactSearch: payload.contactSearch,
        filteredContacts,
      };
    }
    case reducerCases.SET_USER_CONTACTS:
      const contacts = Object.entries(state.conversations)
        .filter(([, messages]) => {
          // Evita que entren al panel de "recientes" conversaciones vacías
          // creadas solo por cambio de canal/edición de info de contacto.
          const hasLastMessage = Boolean(messages?.lastMessage) || Boolean(messages?.dateLastMessage);
          const hasUnread = Number(messages?.messagesUnread || 0) > 0;
          const hasRoom = Boolean(messages?.roomId);
          const isProcess = Boolean(messages?.isProcess);
          const isPinned = Boolean(messages?.pinned);
          const hasTag = Boolean(messages?.tagId);
          return hasLastMessage || hasUnread || hasRoom || isProcess || isPinned || hasTag;
        })
        .map(
        ([key, messages]) => {
          const parsed = splitChatKey(key);
          const phone = messages.phone || parsed.phone || key;
          const channel = messages.channel || parsed.channel || null;
          return {
          phone,
          chatKey: key,
          agent: messages.agent,
          channel,
          channelName: messages.channelName,
          name: messages.name,
          username: messages.username || null,
          userId: messages.userId || null,
          agentName: messages.agentName,
          status: messages.status,
          informacioncontacto: messages.informacioncontacto,
          pinned: messages.pinned || false,
          roomId: messages.roomId,
          tagId: messages.tagId || null,
          internalNoteAlert: messages.internalNoteAlert || null,
          dateLastMessage: messages.dateLastMessage, // ✅ Incluir fecha para ordenamiento
          messagesUnread: messages.messagesUnread || 0, // ✅ Incluir contador de no leídos
        };
        }
      );

      return {
        ...state,
        userContacts: contacts,
      };
    case reducerCases.ADD_OR_UPDATE_CONTACT: {
      // Agregar o actualizar un contacto en userContacts sin tocar conversations
      const { contact } = payload;
      const contactChatKey = contact.chatKey || getChatKey(contact.phone, contact.channel);
      const existingContactIndex = state.userContacts.findIndex(
        (c) => (c.chatKey && c.chatKey === contactChatKey) || (c.phone === contact.phone && c.channel === contact.channel)
      );
      
      const contactData = {
        phone: contact.phone,
        chatKey: contactChatKey,
        agent: contact.agent || null,
        channel: contact.channel || null,
        channelName: contact.channelName || null,
        name: contact.name || contact.phone,
        username: contact.username || null,
        userId: contact.userId || null,
        agentName: contact.agentName || null,
        status: contact.status || null,
        informacioncontacto: contact.informacioncontacto || {},
        pinned: contact.pinned || false,
        roomId: contact.roomId || null,
        tagId: contact.tagId || null,
        dateLastMessage: contact.dateLastMessage || null,
        messagesUnread: contact.messagesUnread || 0,
      };

      let updatedUserContacts;
      if (existingContactIndex >= 0) {
        // Actualizar contacto existente
        updatedUserContacts = [...state.userContacts];
        updatedUserContacts[existingContactIndex] = contactData;
      } else {
        // Agregar nuevo contacto
        updatedUserContacts = [...state.userContacts, contactData];
      }

      return {
        ...state,
        userContacts: updatedUserContacts,
      };
    }
    case reducerCases.SET_SOCKET:
      return {
        ...state,
        socket: payload.socket,
      };
    case reducerCases.UPDATE_UNREAD_MESSAGE:
      {
      const unreadChatKey = resolveChatKey(state, payload);
      const parsedUnread = splitChatKey(unreadChatKey);
      const isIncomingMessage = Number(payload.source) === 1;
      const nextUnreadCount =
        state.current_phone === unreadChatKey || !isIncomingMessage
          ? 0
          : (state.conversations[unreadChatKey]?.messagesUnread || 0) + 1;
      const updatedConversationsForUnread = {
        ...state.conversations,
        [unreadChatKey]: {
          ...state.conversations[unreadChatKey],
          phone: state.conversations[unreadChatKey]?.phone || payload.phone || parsedUnread.phone,
          channel: state.conversations[unreadChatKey]?.channel || payload.channel || parsedUnread.channel,
          chatKey: unreadChatKey,
          lastMessage: payload.message,
          dateLastMessage: payload.created_at,
          sourceLastMessage: payload.source,
          messagesUnread: nextUnreadCount,
        },
      };

      // Actualizar userContacts con la nueva fecha para reordenar
      const updatedUserContactsWithDate = state.userContacts.map(contact =>
        (contact.chatKey || getChatKey(contact.phone, contact.channel)) === unreadChatKey
          ? {
            ...contact,
            dateLastMessage: payload.created_at,
            messagesUnread: nextUnreadCount
          }
          : contact
      );

      return {
        ...state,
        conversations: updatedConversationsForUnread,
        userContacts: updatedUserContactsWithDate,
      };
      }
    case reducerCases.UPDATE_CONVERSATION_PINNED:
      {
      const pinnedChatKey = resolveChatKey(state, payload);
      const updatedConversationsForPinned = {
        ...state.conversations,
        [pinnedChatKey]: {
          ...state.conversations[pinnedChatKey],
          pinned: payload.pinned,
        },
      };

      // Actualizar userContacts y reordenar
      const updatedUserContacts = state.userContacts.map(contact =>
        (contact.chatKey || getChatKey(contact.phone, contact.channel)) === pinnedChatKey
          ? { ...contact, pinned: payload.pinned }
          : contact
      );

      // Separar fijados y no fijados
      const pinnedContacts = updatedUserContacts.filter(c => c.pinned);
      const unpinnedContacts = updatedUserContacts.filter(c => !c.pinned);

      // Ordenar no fijados por fecha del último mensaje (más reciente primero)
      unpinnedContacts.sort((a, b) => {
        const keyA = a.chatKey || getChatKey(a.phone, a.channel);
        const keyB = b.chatKey || getChatKey(b.phone, b.channel);
        const dateA = updatedConversationsForPinned[keyA]?.dateLastMessage || 0;
        const dateB = updatedConversationsForPinned[keyB]?.dateLastMessage || 0;
        return dateB - dateA;
      });

      // Combinar: fijados primero, luego no fijados ordenados
      const reorderedContacts = [...pinnedContacts, ...unpinnedContacts];

      return {
        ...state,
        conversations: updatedConversationsForPinned,
        userContacts: reorderedContacts,
      };
      }
    case reducerCases.SET_MOBILE:
      return {
        ...state,
        mobile: payload.mobile,
      };
    case reducerCases.CHAT_LIST_PAGE:
      return {
        ...state,
        chatListPage: payload.chatListPage,
      };
    case reducerCases.MOVE_CONTACT:
      const clonedContacts = [...state.userContacts];
      const moveChatKey = resolveChatKey(state, payload);
      const contactIndex = clonedContacts.findIndex(
        (contact) => (contact.chatKey || getChatKey(contact.phone, contact.channel)) === moveChatKey
      );

      if (contactIndex === -1) return state; // Contacto no encontrado

      const contactToMove = clonedContacts[contactIndex];

      // Si el contacto está fijado, no lo movemos (ya está en su posición correcta)
      if (contactToMove.pinned) {
        return state;
      }

      // Remover el contacto de su posición actual
      clonedContacts.splice(contactIndex, 1);

      // Encontrar la primera posición después de los contactos fijados
      const firstUnpinnedIndex = clonedContacts.findIndex(c => !c.pinned);

      // Si todos están fijados, agregar al final
      if (firstUnpinnedIndex === -1) {
        clonedContacts.push(contactToMove);
      } else {
        // Insertar justo después del último contacto fijado
        clonedContacts.splice(firstUnpinnedIndex, 0, contactToMove);
      }

      return {
        ...state,
        userContacts: clonedContacts,
      };
    case reducerCases.SET_FILE_CHAT:
      return {
        ...state,
        file_chat: payload.file_chat,
      };
    case reducerCases.CLOSE_CHAT:
      {
      const explicitChatKey = normalizeValue(payload.chatKey);
      const payloadPhone = normalizeValue(payload.phone);
      const payloadChannel = normalizeValue(payload.channel);

      let closeChatKey = "";
      if (explicitChatKey && explicitChatKey.includes("::")) {
        closeChatKey = explicitChatKey;
      } else if (payloadPhone && payloadChannel) {
        const phoneOnly = splitChatKey(payloadPhone).phone || payloadPhone;
        closeChatKey = getChatKey(phoneOnly, payloadChannel);
      } else if (explicitChatKey && state.conversations[explicitChatKey]) {
        closeChatKey = explicitChatKey;
      } else {
        closeChatKey = resolveChatKey(state, payload);
      }

      if (!closeChatKey) {
        return state;
      }

      const newConversations = { ...state.conversations };
      delete newConversations[closeChatKey];

      const updatedContacts = Object.entries(newConversations).map(
        ([key, messages]) => {
          const parsed = splitChatKey(key);
          return {
          phone: messages.phone || parsed.phone || key,
          chatKey: key,
          agent: messages.agent,
          channel: messages.channel || parsed.channel,
          channelName: messages.channelName,
          name: messages.name,
          agentName: messages.agentName,
          status: messages.status,
          informacioncontacto: messages.informacioncontacto,
          pinned: messages.pinned || false,
          roomId: messages.roomId,
          tagId: messages.tagId || null,
          dateLastMessage: messages.dateLastMessage,
          messagesUnread: messages.messagesUnread || 0,
          };
        }
      );

      const updatedFilteredContacts = state.filteredContacts
        ? state.filteredContacts.filter(
            (contact) => (contact.chatKey || getChatKey(contact.phone, contact.channel)) !== closeChatKey
          )
        : state.filteredContacts;

      return {
        ...state,
        conversations: newConversations,
        userContacts: updatedContacts,
        filteredContacts: updatedFilteredContacts,
      };
      }
    case reducerCases.SET_REPLY_MESSAGE:
      return {
        ...state,
        replyMessage: payload.replyMessage,
      };
    case reducerCases.CLEAR_REPLY_MESSAGE:
      return {
        ...state,
        replyMessage: null,
      };
    case reducerCases.SET_CHAT_EXPIRED:
      return {
        ...state,
        isChatExpired: action.isChatExpired,
      };
    case reducerCases.SET_ASSIGNED_AGENT:
      return {
        ...state,
        assignedAgent: action.assignedAgent,
      };
    case reducerCases.SET_NOTE_ALERT: {
      const key = action.chatKey;
      if (!key) return state;
      const conversations = { ...state.conversations };
      const parsed = splitChatKey(key);
      if (!conversations[key]) {
        conversations[key] = {
          messages: [],
          messagesUnread: 0,
          phone: parsed.phone,
          channel: parsed.channel,
          chatKey: key,
        };
      }
      conversations[key] = {
        ...conversations[key],
        internalNoteAlert: action.alert,
        roomId: action.roomId || conversations[key].roomId,
      };
      const userContacts = (state.userContacts || []).map((contact) =>
        (contact.chatKey || getChatKey(contact.phone, contact.channel)) === key
          ? { ...contact, internalNoteAlert: action.alert }
          : contact
      );
      return {
        ...state,
        conversations,
        userContacts,
        notePrompt: action.alert ? { ...action.alert, chatKey: key } : null,
      };
    }
    case reducerCases.CLEAR_NOTE_ALERT: {
      const conversations = { ...state.conversations };
      const key = action.chatKey;
      if (key && conversations[key]) {
        conversations[key] = { ...conversations[key], internalNoteAlert: null };
      }
      const userContacts = (state.userContacts || []).map((contact) =>
        (contact.chatKey || getChatKey(contact.phone, contact.channel)) === key
          ? { ...contact, internalNoteAlert: null }
          : contact
      );
      return { ...state, conversations, userContacts };
    }
    case reducerCases.SET_GLOBAL_STATUS:
      return {
        ...state,
        globalStatus: action.globalStatus,
      };
    case reducerCases.SET_CONNECTED_AGENTS:
      return {
        ...state,
        connectedAgents: action.agents,
      };
    case reducerCases.UPDATE_MESSAGE_REACTIONS:
      const conversationsWithReactions = { ...state.conversations };

      // Buscar el mensaje en todas las conversaciones y actualizar sus reacciones
      Object.keys(conversationsWithReactions).forEach((phone) => {
        const messages = conversationsWithReactions[phone].messages;
        if (Array.isArray(messages)) {
          const messageIndex = messages.findIndex(
            (msg) => msg._id === payload.messageId
          );
          if (messageIndex !== -1) {
            messages[messageIndex].reactions = payload.reactions;
          }
        }
      });

      return {
        ...state,
        conversations: conversationsWithReactions,
      };

    case reducerCases.UPDATE_MESSAGE_ERROR:
      {
      const errorChatKey = resolveChatKey(state, payload);
      const updatedConversationsWithError = { ...state.conversations };
      const updatedPendingErrorsForError = { ...state.pendingMessageErrors };

      // Verificar si la conversación existe
      if (!updatedConversationsWithError[errorChatKey]) {
        // Guardar el error como pendiente para aplicarlo cuando llegue el mensaje
        updatedPendingErrorsForError[payload.messageId] = {
          phone: payload.phone,
          error: payload.error
        };
        console.log(`[UPDATE_MESSAGE_ERROR] Error guardado como pendiente para messageId: ${payload.messageId}`);
        return {
          ...state,
          pendingMessageErrors: updatedPendingErrorsForError,
        };
      }

      const messages =
        updatedConversationsWithError[errorChatKey]?.messages || [];

      let messageFound = false;
      const updatedMessages = messages.map((msg) => {
        // Saltar mensajes de fecha
        if (msg.msgtype === "date") {
          return msg;
        }

        // Búsqueda robusta por ID (similar a UPDATE_MESSAGE_STATUS)
        const msgIdStr = msg._id?.toString();
        const payloadIdStr = payload.messageId?.toString();
        const matchesId =
          payload.messageId &&
          (msg._id === payload.messageId ||
            msgIdStr === payloadIdStr ||
            msgIdStr === payload.messageId ||
            msg._id === payloadIdStr);

        if (matchesId) {
          messageFound = true;
          // Remover de errores pendientes si existe
          if (updatedPendingErrorsForError[payload.messageId]) {
            delete updatedPendingErrorsForError[payload.messageId];
          }
          return {
            ...msg,
            error: true,
            errorstatus: payload.error
          };
        }
        return msg;
      });

      if (!messageFound) {
        // Guardar el error como pendiente
        updatedPendingErrorsForError[payload.messageId] = {
          phone: payload.phone,
          error: payload.error
        };
        console.log(`[UPDATE_MESSAGE_ERROR] Mensaje no encontrado, error guardado como pendiente:`, {
          messageId: payload.messageId,
          phone: payload.phone,
          totalMessages: messages.filter(m => m.msgtype !== "date").length,
        });
      }

      updatedConversationsWithError[errorChatKey].messages = updatedMessages;

      // Si el mensaje actualizado es el último mensaje enviado, actualizar lastMessage
      const lastSentMsg = updatedMessages
        .filter((m) => m.msgtype !== "date" && m.source !== 1)
        .slice(-1)[0];
      const errorConv = updatedConversationsWithError[errorChatKey];
      const errorMatchesStoredLast =
        errorConv.lastMessageId &&
        payload.messageId &&
        String(errorConv.lastMessageId) === String(payload.messageId);
      if (
        (lastSentMsg && lastSentMsg._id?.toString() === payload.messageId?.toString()) ||
        errorMatchesStoredLast
      ) {
        updatedConversationsWithError[errorChatKey] = {
          ...errorConv,
          errorLastMessage: payload.error,
        };
      }

      return {
        ...state,
        conversations: updatedConversationsWithError,
        pendingMessageErrors: updatedPendingErrorsForError,
      };
      }
    case reducerCases.CLEAR_MESSAGE_ERROR:
      {
      const clearChatKey = resolveChatKey(state, payload);
      const clearConversation = state.conversations?.[clearChatKey];
      if (!clearConversation) return state;
      const clearId = payload.messageId?.toString();
      const clearedMessages = (clearConversation.messages || []).map((msg) =>
        msg.msgtype !== "date" && msg._id?.toString() === clearId
          ? { ...msg, error: false, errorstatus: null, status: "error" }
          : msg
      );
      const { [clearId]: _cleared, ...pendingMessageErrors } = state.pendingMessageErrors || {};
      return {
        ...state,
        pendingMessageErrors,
        conversations: {
          ...state.conversations,
          [clearChatKey]: { ...clearConversation, messages: clearedMessages, errorLastMessage: null },
        },
      };
      }
    case reducerCases.UPDATE_MESSAGE_STATUS:
      {
      const statusChatKey = resolveChatKey(state, payload);
      const updatedConversationsWithStatus = { ...state.conversations };

      if (!updatedConversationsWithStatus[statusChatKey]) {
        return state;
      }

      const messagesWithStatus =
        updatedConversationsWithStatus[statusChatKey]?.messages || [];

      const updatedMessagesWithStatus = messagesWithStatus.map((msg) => {
        const matchesWaid = payload.waid && msg.waid === payload.waid;
        const msgIdStr = msg._id?.toString();
        const payloadIdStr = payload.messageId?.toString();
        const matchesId =
          payload.messageId &&
          (msg._id === payload.messageId ||
            msgIdStr === payloadIdStr ||
            msgIdStr === payload.messageId ||
            msg._id === payloadIdStr);

        if (matchesWaid || matchesId) {
          return { ...msg, status: payload.status };
        }
        return msg;
      });

      updatedConversationsWithStatus[statusChatKey].messages =
        updatedMessagesWithStatus;

      // Si el mensaje actualizado es el último mensaje enviado, actualizar statusLastMessage
      const lastSentMsgWithStatus = updatedMessagesWithStatus
        .filter((m) => m.msgtype !== "date" && m.source !== 1)
        .slice(-1)[0];
      const statusConv = updatedConversationsWithStatus[statusChatKey];
      const matchesLoadedLast =
        lastSentMsgWithStatus &&
        (lastSentMsgWithStatus._id?.toString() === payload.messageId?.toString() ||
          (payload.waid && lastSentMsgWithStatus.waid === payload.waid));
      const matchesStoredLast =
        statusConv.lastMessageId &&
        payload.messageId &&
        String(statusConv.lastMessageId) === String(payload.messageId);
      if (
        (matchesLoadedLast || matchesStoredLast) &&
        statusRank(payload.status) >= statusRank(statusConv.statusLastMessage)
      ) {
        updatedConversationsWithStatus[statusChatKey] = {
          ...statusConv,
          statusLastMessage: payload.status,
        };
      }

      return {
        ...state,
        conversations: updatedConversationsWithStatus,
      };
      }
    case reducerCases.UPDATE_DATE_CONTROL:
      if (!state.current_phone) {
        // Si current_phone es una cadena vacía, simplemente retorna el estado sin modificar
        return state;
      }
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [state.current_phone]: {
            ...state.conversations[state.current_phone],
            dateControl: action.dateControl,
          },
        },
      };
    case reducerCases.SET_ACTIVE_FILTERS:
      return {
        ...state,
        activeFilters: action.filters,
      };
    case reducerCases.SET_LOADING_CONVERSATIONS:
      return {
        ...state,
        isLoadingConversations: payload.isLoading,
      };
    case reducerCases.SET_ACTIVE_LIST_FILTER:
      return {
        ...state,
        activeListFilter: action.value,
        // si cambiamos de filtro, limpiamos el tag activo salvo que sea 'tags'
        activeTagFilter: action.value === "tags" ? state.activeTagFilter : null,
        // Si cambiamos de filtro, limpiar el filtro de la conversación activa
        activeConversationFilter: null,
      };
    case reducerCases.SET_ACTIVE_TAG_FILTER:
      return {
        ...state,
        activeTagFilter: action.tagId,
        // Si cambiamos el tag filter, limpiar el filtro de la conversación activa
        activeConversationFilter: null,
      };
    case reducerCases.SET_TAGS_PAGE:
      return {
        ...state,
        tagsPage: !state.tagsPage,
        contactsPage: false,
      };
    case reducerCases.CHANGE_CURRENT_CHAT_USER:
      {
      const changedChatKey = getChatKey(action.user.phone, action.user.channel);
      return {
        ...state,
        current_phone: changedChatKey,
        currentChatUser: {
          ...action.user,
          chatKey: changedChatKey,
        },
      };
      }
    case reducerCases.SET_MESSAGES:
      // Actualizar mensajes dentro de la conversación específica
      {
      const setMessagesChatKey = resolveChatKey(state, action);
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [setMessagesChatKey]: {
            ...state.conversations?.[setMessagesChatKey],
            messages: action.messages,
          },
        },
      };
      }
    case reducerCases.SET_TARGET_MESSAGE_ID:
      return {
        ...state,
        targetMessageId: action.messageId,
      };
    case reducerCases.UPDATE_CONVERSATION_TAG:
      {
      const tagChatKey = resolveChatKey(state, action);
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [tagChatKey]: {
            ...state.conversations?.[tagChatKey],
            tagId: action.tagId,
          },
        },
        userContacts: state.userContacts.map(contact =>
          (contact.chatKey || getChatKey(contact.phone, contact.channel)) === tagChatKey
            ? { ...contact, tagId: action.tagId }
            : contact
        ),
      };
      }
    case reducerCases.UPDATE_CONVERSATION_UNREAD:
      {
      const unreadConversationChatKey = resolveChatKey(state, action);
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [unreadConversationChatKey]: {
            ...state.conversations?.[unreadConversationChatKey],
            messagesUnread: action.messagesUnread || 0,
          },
        },
        userContacts: state.userContacts.map(contact =>
          (contact.chatKey || getChatKey(contact.phone, contact.channel)) === unreadConversationChatKey
            ? { ...contact, messagesUnread: action.messagesUnread || 0 }
            : contact
        ),
      };
      }
    case reducerCases.SET_FORWARD_MESSAGE:
      return {
        ...state,
        forwardMessage: action.forwardMessage,
      };
    case reducerCases.CLEAR_FORWARD_MESSAGE:
      return {
        ...state,
        forwardMessage: null,
      };
    case reducerCases.SET_TYPING_USER:
      {
      const typingChatKey = resolveChatKey(state, action);
      if (action.agentName) {
        return {
          ...state,
          typingUsers: {
            ...state.typingUsers,
            [typingChatKey]: { agentName: action.agentName, timestamp: Date.now() },
          },
        };
      }
      // Clear typing for this chatKey
      const { [typingChatKey]: _removed, ...restTyping } = state.typingUsers;
      return {
        ...state,
        typingUsers: restTyping,
      };
      }
    case reducerCases.SET_CRM_OPEN:
      return {
        ...state,
        crmOpen: action.crmOpen ?? !state.crmOpen,
        crmView: action.crmView ?? state.crmView,
      };
    case reducerCases.SET_CRM_VIEW:
      return {
        ...state,
        crmView: action.crmView,
        crmOpen: true,
      };
    default:
      return state;
  }
};

export default reducer;



